import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";

const CHANNELS = [
  "Instagram",
  "Facebook",
  "TikTok",
  "YouTube",
  "Google Ads",
  "Google Organic",
  "Collaborator",
  "Website Normal",
  "Direct",
];

export default function TrackingAdmin() {
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState({ total: 0, byChannel: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    channel: "", source: "", campaign: "", vehicleId: "", collaborator: "",
    date_from: "", date_to: "",
  });
  const [datePreset, setDatePreset] = useState(""); // "" | "year" | "month" | "week" | "custom"
  const [presetYear, setPresetYear] = useState(new Date().getFullYear());
  const [presetMonth, setPresetMonth] = useState(new Date().getMonth() + 1); // 1-12

  const load = async (override) => {
    setLoading(true);
    try {
      const f = override || filters;
      const params = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => v && params.set(k, v));
      const [evRes, sumRes] = await Promise.all([
        api.get(`/tracking/events?${params.toString()}`),
        api.get(`/tracking/summary?${params.toString()}`),
      ]);
      setEvents(evRes.data || []);
      setSummary(sumRes.data || { total: 0, byChannel: {} });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const apply = () => load();
  const reset = async () => {
    const cleared = { channel: "", source: "", campaign: "", vehicleId: "", collaborator: "", date_from: "", date_to: "" };
    setFilters(cleared);
    setDatePreset("");
    await load(cleared);
  };

  // Date preset helpers — compute YYYY-MM-DD strings (UTC).
  const fmt = (d) => d.toISOString().slice(0, 10);
  const applyDatePreset = async (preset) => {
    setDatePreset(preset);
    const now = new Date();
    let from = "", to = "";
    if (preset === "year") {
      from = `${presetYear}-01-01`;
      to = `${presetYear}-12-31`;
    } else if (preset === "month") {
      const m = String(presetMonth).padStart(2, "0");
      const lastDay = new Date(presetYear, presetMonth, 0).getDate();
      from = `${presetYear}-${m}-01`;
      to = `${presetYear}-${m}-${String(lastDay).padStart(2, "0")}`;
    } else if (preset === "last7") {
      const start = new Date(now); start.setDate(start.getDate() - 6);
      from = fmt(start); to = fmt(now);
    } else if (preset === "week") {
      // Monday of current week
      const start = new Date(now);
      const dow = (start.getDay() + 6) % 7; // 0 = Monday
      start.setDate(start.getDate() - dow);
      const end = new Date(start); end.setDate(end.getDate() + 6);
      from = fmt(start); to = fmt(end);
    } else if (preset === "lastWeek") {
      const start = new Date(now);
      const dow = (start.getDay() + 6) % 7;
      start.setDate(start.getDate() - dow - 7);
      const end = new Date(start); end.setDate(end.getDate() + 6);
      from = fmt(start); to = fmt(end);
    }
    const next = { ...filters, date_from: from, date_to: to };
    setFilters(next);
    if (preset !== "custom") await load(next);
  };

  const exportCsv = () => {
    if (!events.length) return;
    const cols = [
      "createdAt", "channel", "source", "medium", "campaign", "ref", "gclid",
      "vehicleName", "vehicleId", "language", "pickupDate", "returnDate",
      "destinationWaMe", "landingPage", "currentPage", "deviceType",
    ];
    const esc = (v) => {
      const s = (v ?? "").toString().replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const csv = [cols.join(","), ...events.map((e) => cols.map((c) => esc(e[c])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `slr-tracking-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const channelTotals = useMemo(() => {
    const out = {};
    CHANNELS.forEach((c) => { out[c] = summary.byChannel?.[c] || 0; });
    return out;
  }, [summary]);

  return (
    <div data-testid="admin-tracking-page">
      <h1 className="font-display text-[#F5F1EB] text-3xl mb-2">Tracking</h1>
      <div className="flex items-center justify-between mb-8">
        <p className="text-[#8A8A8A] text-sm">Origen de los clics a WhatsApp · UTMs, gclid, fbclid, referrer.</p>
        <button
          onClick={exportCsv}
          disabled={!events.length}
          className="px-4 h-10 border border-[#C9A55A] text-[#C9A55A] hover:bg-[#C9A55A] hover:text-[#0C0C0C] text-xs uppercase tracking-[0.18em] disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="tk-export-csv">
          Export CSV
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Stat label="Total" value={summary.total} testid="tk-total" highlight />
        {CHANNELS.map((c) => (
          <Stat key={c} label={c} value={channelTotals[c]} testid={`tk-${c.toLowerCase().replace(/\s+/g, "-")}`} />
        ))}
      </div>

      <div className="border border-[#2A2A2A] p-4 mb-3" data-testid="tk-date-filters">
        <div className="text-overline text-[#C9A55A] mb-3">Filtros por fecha</div>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <Label>Año</Label>
            <select value={presetYear} onChange={(e) => setPresetYear(Number(e.target.value))} data-testid="tk-date-year"
              className="bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-2 h-10 text-sm">
              {Array.from({ length: 5 }).map((_, i) => {
                const y = new Date().getFullYear() - i;
                return <option key={y} value={y}>{y}</option>;
              })}
            </select>
          </div>
          <div>
            <Label>Mes</Label>
            <select value={presetMonth} onChange={(e) => setPresetMonth(Number(e.target.value))} data-testid="tk-date-month"
              className="bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-2 h-10 text-sm">
              {["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"].map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <button onClick={() => applyDatePreset("year")} className={`px-3 h-10 border text-[11px] uppercase tracking-[0.18em] ${datePreset === "year" ? "border-[#C9A55A] text-[#C9A55A]" : "border-[#2A2A2A] text-[#F5F1EB]"}`} data-testid="tk-preset-year">Año</button>
          <button onClick={() => applyDatePreset("month")} className={`px-3 h-10 border text-[11px] uppercase tracking-[0.18em] ${datePreset === "month" ? "border-[#C9A55A] text-[#C9A55A]" : "border-[#2A2A2A] text-[#F5F1EB]"}`} data-testid="tk-preset-month">Mes</button>
          <button onClick={() => applyDatePreset("week")} className={`px-3 h-10 border text-[11px] uppercase tracking-[0.18em] ${datePreset === "week" ? "border-[#C9A55A] text-[#C9A55A]" : "border-[#2A2A2A] text-[#F5F1EB]"}`} data-testid="tk-preset-week">Semana</button>
          <button onClick={() => applyDatePreset("lastWeek")} className={`px-3 h-10 border text-[11px] uppercase tracking-[0.18em] ${datePreset === "lastWeek" ? "border-[#C9A55A] text-[#C9A55A]" : "border-[#2A2A2A] text-[#F5F1EB]"}`} data-testid="tk-preset-last-week">Semana ant.</button>
          <button onClick={() => applyDatePreset("last7")} className={`px-3 h-10 border text-[11px] uppercase tracking-[0.18em] ${datePreset === "last7" ? "border-[#C9A55A] text-[#C9A55A]" : "border-[#2A2A2A] text-[#F5F1EB]"}`} data-testid="tk-preset-last7">Últimos 7d</button>
          <div className="flex items-end gap-2">
            <div>
              <Label>Desde</Label>
              <input type="date" value={filters.date_from} onChange={(e) => { setFilters({ ...filters, date_from: e.target.value }); setDatePreset("custom"); }} data-testid="tk-date-from"
                className="bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-2 h-10 text-sm" />
            </div>
            <div>
              <Label>Hasta</Label>
              <input type="date" value={filters.date_to} onChange={(e) => { setFilters({ ...filters, date_to: e.target.value }); setDatePreset("custom"); }} data-testid="tk-date-to"
                className="bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-2 h-10 text-sm" />
            </div>
          </div>
        </div>
        {(filters.date_from || filters.date_to) && (
          <div className="mt-2 text-[11px] text-[#8A8A8A]">
            Filtro activo: {filters.date_from || "—"} → {filters.date_to || "—"}
          </div>
        )}
      </div>

      <div className="border border-[#2A2A2A] p-4 mb-8">
        <div className="text-overline text-[#C9A55A] mb-3">Filtros</div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <Select label="Channel" value={filters.channel} onChange={(v) => setFilters({ ...filters, channel: v })} options={["", ...CHANNELS]} testid="tk-flt-channel" />
          <TextField label="utm_source" value={filters.source} onChange={(v) => setFilters({ ...filters, source: v })} testid="tk-flt-source" />
          <TextField label="utm_campaign" value={filters.campaign} onChange={(v) => setFilters({ ...filters, campaign: v })} testid="tk-flt-campaign" />
          <TextField label="collaborator" value={filters.collaborator} onChange={(v) => setFilters({ ...filters, collaborator: v })} testid="tk-flt-collaborator" />
          <TextField label="vehicleId" value={filters.vehicleId} onChange={(v) => setFilters({ ...filters, vehicleId: v })} testid="tk-flt-vehicle" />
          <div className="flex items-end gap-2">
            <button onClick={apply} className="btn-gold flex-1" data-testid="tk-apply">Apply</button>
            <button onClick={reset} className="px-3 h-10 border border-[#2A2A2A] text-[#F5F1EB] text-xs uppercase tracking-[0.18em]" data-testid="tk-reset">Reset</button>
          </div>
        </div>
      </div>

      <div className="border border-[#2A2A2A]" data-testid="tk-table-wrap">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[#2A2A2A] text-[10px] tracking-[0.18em] uppercase text-[#C9A55A]">
          <div className="col-span-2">Date</div>
          <div className="col-span-2">Vehicle</div>
          <div className="col-span-1">Lang</div>
          <div className="col-span-2">Channel</div>
          <div className="col-span-2">Campaign</div>
          <div className="col-span-1">Ref</div>
          <div className="col-span-1">Device</div>
          <div className="col-span-1">WA</div>
        </div>
        {loading && <div className="p-6 text-[#8A8A8A] text-sm">Loading…</div>}
        {!loading && events.length === 0 && <div className="p-6 text-[#8A8A8A] text-sm" data-testid="tk-empty">No events yet.</div>}
        {!loading && events.map((e) => (
          <div key={e.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[#2A2A2A] last:border-b-0 text-xs text-[#F5F1EB]" data-testid={`tk-row-${e.id}`}>
            <div className="col-span-2 text-[#8A8A8A]">{e.createdAt?.slice(0, 19).replace("T", " ")}</div>
            <div className="col-span-2 truncate">{e.vehicleName || "—"}</div>
            <div className="col-span-1 uppercase text-[#C9A55A]">{e.language || "—"}</div>
            <div className="col-span-2"><Badge color={channelColor(e.channel)}>{e.channel || "Unknown"}</Badge></div>
            <div className="col-span-2 truncate text-[#8A8A8A]">{e.campaign || "—"}</div>
            <div className="col-span-1 truncate">{e.ref || "—"}</div>
            <div className="col-span-1 text-[#8A8A8A]">{e.deviceType || "—"}</div>
            <div className="col-span-1 text-[#8A8A8A] truncate">{e.destinationWaMe || "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function channelColor(c) {
  switch (c) {
    case "Instagram": return "#E1306C";
    case "Facebook": return "#1877F2";
    case "TikTok": return "#69C9D0";
    case "YouTube": return "#FF0000";
    case "Google Ads": return "#4285F4";
    case "Google Organic": return "#34A853";
    default: return "#C9A55A";
  }
}

const Label = ({ children }) => <label className="text-[10px] text-[#8A8A8A] tracking-[0.18em] uppercase block mb-1">{children}</label>;
function Stat({ label, value, testid, highlight }) {
  return (
    <div className={`border ${highlight ? "border-[#C9A55A]" : "border-[#2A2A2A]"} p-4`} data-testid={testid}>
      <div className="text-[10px] text-[#8A8A8A] tracking-[0.18em] uppercase">{label}</div>
      <div className={`font-display text-2xl mt-1 ${highlight ? "text-[#C9A55A]" : "text-[#F5F1EB]"}`}>{value}</div>
    </div>
  );
}
function TextField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 h-10 text-sm" />
    </div>
  );
}
function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 h-10 text-sm">
        {options.map((o) => <option key={o} value={o}>{o || "— All —"}</option>)}
      </select>
    </div>
  );
}
function Badge({ children, color }) {
  return (
    <span className="inline-block text-[10px] tracking-[0.14em] uppercase px-2 py-0.5 border" style={{ color, borderColor: `${color}55` }}>
      {children}
    </span>
  );
}
